import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import { CookieBanner } from "@/components/CookieBanner";
import { ChatPanel } from "@/components/ChatPanel";
import {
  gaEvent, gaEventOnce, analyticsSuppressed, initEngagement, setSegment,
  noteLoopStep, noteLegalQuestion, noteComplianceReached, engagementCounts, analyticsSessionId,
} from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- motion system: 12px rise + fade, 400ms ease-out, 60ms stagger ---------- */

const fade = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.4, ease: "easeOut" },
};

const fadeAt = (i) => ({
  ...fade,
  transition: { ...fade.transition, delay: i * 0.06 },
});

const Hairline = ({ className = "" }) => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: "easeOut" }}
    className={`h-px bg-[#2E2E2E] origin-left ${className}`}
    aria-hidden="true"
  />
);

const track = (event, meta = {}) => {
  if (analyticsSuppressed()) return;
  fetch(`${API}/analytics/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event, meta, session_id: analyticsSessionId() }),
  }).catch(() => {});
};

/* ---------- copy ---------- */

const CAPABILITIES = [
  {
    label: "DATA OPERATIONS",
    body: "Large consumer and business files — cleaned, completed, and made reachable again.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#DC2626" strokeWidth="1">
        <ellipse cx="14" cy="7" rx="9" ry="3.5" />
        <path d="M5 7v14c0 1.93 4.03 3.5 9 3.5s9-1.57 9-3.5V7" />
        <path d="M5 14c0 1.93 4.03 3.5 9 3.5s9-1.57 9-3.5" />
      </svg>
    ),
  },
  {
    label: "INFRASTRUCTURE & SYSTEMS",
    body: "The systems an organization actually runs on, including the ones nobody owns yet.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#DC2626" strokeWidth="1">
        <circle cx="6" cy="6" r="3" />
        <circle cx="22" cy="6" r="3" />
        <circle cx="14" cy="22" r="3" />
        <path d="M9 6h10M7.5 8.5l5 11M20.5 8.5l-5 11" />
      </svg>
    ),
  },
  {
    label: "COMPLIANCE & CONSENT",
    body: "Consent captured with proof, lists screened before anyone dials, records you can produce.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#DC2626" strokeWidth="1">
        <path d="M7 3.5h14v21H7z" />
        <path d="M10.5 8h7M10.5 12h7M10.5 16h3.5" />
        <path d="M15.5 20.5l1.8 1.8 3.2-3.6" />
      </svg>
    ),
  },
];

/* Proof band — hidden until real figures are supplied. Flip to true to show. */
const SHOW_PROOF_BAND = false;
const PROOF_FIGURES = [
  { figure: "12+", caption: "Years running data operations at national scale" },
  { figure: "40M+", caption: "Records cleaned, completed, and made reachable" },
  { figure: "50", caption: "States of privacy and messaging rules tracked" },
];

const LOOP_OUT = [
  { n: "1", lead: "I clean the file.", body: "Every record checked against national address data, duplicates merged, and people who have passed away quietly removed. You stop paying to reach people who are not there." },
  { n: "2", lead: "I fill in what's missing.", body: "A name with no phone number is a dead end. I add missing emails, phone numbers, and household detail, so the people already on your list become reachable again." },
  { n: "3", lead: "I find who's already interested.", body: "Most people who visit your site leave without filling anything in. I can identify a meaningful share of them, and I can see who is actively researching what you offer — often before they've contacted anyone." },
  { n: "4", lead: "I get you in front of them.", body: "Your audience goes into the tools you already use, or triggers a physical letter off a digital signal so the mail lands while they're still thinking about it." },
];

const LOOP_BACK = [
  { n: "5", lead: "I capture the response, with proof.", body: "Every form and every call carries a time-stamped record of exactly when and how that person agreed to be contacted. If a regulator, a lawyer, or a client ever asks, you can produce it." },
  { n: "6", lead: "I check it's real before anyone calls.", body: "Screened for bots and fake entries, checked against do-not-call lists, and checked against people who make a living suing companies over unwanted calls. The penalties here run per message, not per campaign." },
  { n: "7", lead: "I route it in seconds.", body: "Leads go to the right person by location, availability, or whatever rule fits how you work. Calls get answered, directed, and tracked, so you know what happened on every one." },
];

const LOOP_STEPS = [...LOOP_OUT, ...LOOP_BACK];

const AUDIENCES = [
  { id: "business", label: "Business" },
  { id: "nonprofit", label: "Non-profit" },
  { id: "campaign", label: "Campaign" },
  { id: "enterprise", label: "Enterprise" },
];

const SCENARIO_GROUPS = {
  business: {
    group: "Businesses selling to consumers",
    anchor: "If any of that sounds like your month, tell me what you're working with.",
    items: [
      { title: "The audience built on a dead list", body: "You're running ads against an audience modeled on a customer file you last touched years ago. A large share of those records are stale — wrong numbers, changed emails, people who moved. The ad platform doesn't tell you that; it just spends the budget. I clean and complete the file before it ever becomes an audience, then model the audience from records that are actually current. The same budget now goes toward people who exist and can be reached, and nothing about your creative had to change." },
      { title: "The lead that went cold in an afternoon", body: "A lead comes in at two o'clock. Someone picks it up at four. By then they've filled out three other forms and spoken to two of your competitors. I route leads to whoever is available the moment they arrive — by territory, by hours, by whatever rule fits how you actually staff. You call while you're still the only one who called." },
      { title: "The letter from a lawyer", body: "A demand letter arrives about a text you sent eight months ago. You don't know where that number came from, whether the person opted in, or who to ask. I capture every submission with a time-stamped record of when and how that person agreed to be contacted, and screen the file against do-not-call lists before anyone dials. You produce the record, and the conversation ends there." },
    ],
  },
  nonprofit: {
    group: "Non-profits and foundations",
    anchor: "If your file is older than your last director, tell me what you're working with.",
    items: [
      { title: "The third letter to a woman who died", body: "Your appeal goes out. A week later a board member forwards you an email from a donor's daughter — her mother passed last year, and this is the third letter you've sent. I screen every mailing for the deceased before it goes out, not once a year when someone remembers. That letter never leaves the building." },
      { title: "The lapsed file you can't reach", body: "You have thousands of lapsed donors and no way to contact most of them. The addresses are years old and a large share of the file has no email at all. I update what's changed and fill in what's missing, so lapsed names become contactable again. Your win-back appeal reaches people who will actually receive it." },
      { title: "Everyone gets the same ask", body: "You're mailing the same letter to your whole file because you have no way to tell who could give more. I build profiles across the file so you can see who warrants a conversation rather than another envelope. Your development director spends the week on a dozen phone calls instead of a dozen thousand letters." },
    ],
  },
  campaign: {
    group: "Campaigns and committees",
    anchor: "If your file is the thing standing between you and the program, tell me what you're working with.",
    items: [
      { title: "A voter file you can't call", body: "You bought the file. Your phone program can only touch a fraction of it, because most records have no number and half the ones that do are landlines. I clean the file and fill in what can be filled in before the program starts. Your callers spend their shift on connects instead of disconnects." },
      { title: "The program stuck in review", body: "Your texting vendor asks for proof of consent on the list you handed over. You don't have it, and now launch is sliding. I capture consent at the point of collection and screen the file before it goes anywhere. The program launches on the date you planned." },
      { title: "Paying to reach people who already gave", body: "Your acquisition ads are running against people who are already on your donor file, and missing people who look exactly like your best givers. I remove your existing donors from acquisition and build the audience off who actually gave. Acquisition money buys new people." },
    ],
  },
  enterprise: {
    group: "The complicated one — enterprise",
    anchor: "If you've been meaning to deal with it for two quarters, tell me what you're working with.",
    items: [
      { title: "Nobody owns it", body: "Something in your stack works, mostly. The person who built it left. It breaks in ways that cost real money and nobody can tell you why, so everyone has quietly built their week around avoiding it. I map what's actually happening, rebuild the parts that can't hold, and answer the compliance questions everyone deferred. It stops being the thing you work around." },
    ],
  },
};

const ENGAGEMENT_STEPS = [
  { lead: "We talk first.", body: "A short conversation about what you're working with and what's actually going wrong. No deck, no discovery fee." },
  { lead: "I scope it in writing.", body: "You get a written scope covering what I'll do, what it depends on, and what it costs. Fixed where it can be fixed." },
  { lead: "Then we start.", body: "Your work gets attention rather than a queue position — that's the point of keeping the calendar short." },
];

const MESSAGING_BLOCKS = [
  { lead: "Getting your messages delivered.", testid: "messaging-registration", body: "Before any program starts, the sending brand and each individual campaign have to be registered with the carriers — this is what the industry calls 10DLC registration for A2P messaging, meaning business-to-consumer texting from a normal ten-digit number. What trips most people up isn't the registration itself; it's that the use case they declared doesn't match the messages they actually send, or the opt-in wording on their form doesn't match what's on file. Carriers check. When it doesn't line up they filter the traffic silently, and a delivery report can look perfectly healthy while a large share of the messages never landed." },
  { lead: "Proving consent.", testid: "messaging-consent", body: "Under the federal rules governing calls and texts — the TCPA — you need to be able to show that each person agreed to be contacted, in the right way, before you contacted them. Inheriting a list is not consent. A checkbox nobody can produce a record of is not consent. I capture consent at the point of collection with a time-stamped record of exactly what the person saw and agreed to, and I keep it in a form you can hand to someone." },
  { lead: "Keeping the file clean.", testid: "messaging-screening", body: "Before anything sends, the list gets checked against do-not-call registries, against known bad actors, and against people who make a living bringing claims over unwanted messages. That last check costs almost nothing and prevents the expensive kind of problem." },
];

const MESSAGING_SCENARIOS = [
  { title: "The messages that never arrived", body: "You send a text program to your whole file. The delivery report looks fine. Almost nobody replies, nothing bounced, and you spend a week wondering whether your message was wrong. I register the sending brand and each campaign properly, and make sure the use case on file matches what you're actually sending. Your messages arrive, and the response rate you were expecting shows up." },
  { title: "Shut off on day four", body: "Your number stops sending partway through a program. No warning, no explanation, and getting it turned back on means restarting a registration you were told was already handled. I get the registration right before the first send and keep the opt-in wording on your form matching what's filed with the carriers. The program runs the whole way through." },
  { title: "The letter about a text", body: "Somebody complains about a message you sent months ago. Then a letter arrives asking you to produce proof that the person agreed to be contacted, on a list you inherited from a vendor who is no longer returning calls. I capture consent at the point of collection with a time-stamped record, and screen the file before anything goes out. You produce the record, and it stops there." },
  { title: "The campaign that couldn't launch", body: "Your messaging vendor asks for consent documentation on the file you handed them. You don't have it in a form they'll accept, launch slides, and the window you were targeting closes. I handle registration and consent documentation before the file ever reaches a vendor. You launch on the date you planned." },
];

const RULEBOOK_FALLBACK = [
  { q: "Where do these people live?", a: "Privacy law in the United States is now written state by state, and the states do not agree with each other. Colorado, California, Virginia, Texas, Connecticut and a growing list each have their own rules on what you must disclose, what people can opt out of, and how quickly you have to honor it — including automated opt-out signals a browser can send on someone's behalf. A national list means you are subject to all of them at once." },
  { q: "How are you reaching them?", a: "Texts and calls sit under the federal rules on consent, and several states have their own versions that are stricter than the federal one. Email has its own regime governing what your header can say, whether your physical address appears, and how fast an unsubscribe has to be honored. Calling carries do-not-call obligations on top of everything else." },
  { q: "What are you going to do with the answer?", a: "This is the question almost nobody gets asked. If enriched data is used to decide eligibility — who gets credit, insurance, housing, or a job — that is regulated as consumer reporting, with a completely different set of duties attached. The same routine addition of data that works for a marketing audience becomes something else entirely when it drives an approval decision." },
  { q: "Who is on this list who shouldn't be?", a: "Some people are protected by name. Several states now restrict publishing or disclosing home addresses and phone numbers for judges, law enforcement, and their families, and the claims arising from it have been substantial. Beyond that there are do-not-call registrations, prior opt-outs you may have inherited, and a small population of people who bring these claims professionally." },
  { q: "What is running on your website?", a: "Identifying visitors and tracking behavior touches disclosure obligations, and in some states it has been argued under old wiretapping and interception statutes rather than modern privacy law. What you disclose, when you disclose it, and what your tags are actually doing all matter. This is the fastest-moving area on this page." },
  { q: "Are you registered where you need to be?", a: "Several states require businesses that trade in consumer data to register as data brokers. Most states require a charity to register before it solicits donations there — and \"there\" means where the donor lives, not where you are. Messaging carriers require their own registration before you send. These are all separate, and none of them is automatic." },
  { q: "Who is paying for this, and does it have to say so?", a: "Political advertising carries disclaimer requirements that vary by jurisdiction and by medium, and the rules for a charitable organization are different again — some organizations face hard limits on the kind of activity they can fund at all. Getting the funding structure wrong is not a marketing problem." },
  { q: "What happens when someone asks you to delete it?", a: "People increasingly have the right to ask what you hold and to have it removed, and the clock starts when they ask. If your data lives in four systems and nobody owns the request, you will miss it. I build the deletion path before it is needed rather than after the first request arrives." },
];

/* ---------- small pieces ---------- */

const Eyebrow = ({ children }) => (
  <p className="smallcaps text-[14px] text-[#FF5C5C]">{children}</p>
);

const SectionHeading = ({ children, id }) => (
  <motion.h2 {...fade} id={id} className="font-display text-[32px] md:text-[40px] leading-[1.12] text-white">
    {children}
  </motion.h2>
);

const AnchorLine = ({ children }) => (
  <motion.p {...fade} className="mt-12">
    <a href="#contact" className="text-[19px] font-medium text-[#FF5C5C] underline underline-offset-4 decoration-1 hover:text-white transition-colors duration-300">
      {children} →
    </a>
  </motion.p>
);

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "What I do", href: "#work" },
  { label: "Compliance", href: "#compliance" },
  { label: "Contact", href: "#contact" },
];

/* ---------- 7-step loop diagram ---------- */

const LoopTitle = ({ s, active, onActivate, index, suffix = "" }) => (
  <motion.button
    {...fadeAt(index)}
    type="button"
    onMouseEnter={() => onActivate(s.n)}
    onFocus={() => onActivate(s.n)}
    onClick={() => onActivate(s.n)}
    aria-pressed={active}
    data-testid={`loop-step-${s.n}${suffix}`}
    className="flex items-baseline gap-4 text-left w-full group"
  >
    <span className="font-display text-[22px] text-[#DC2626] leading-none w-7 shrink-0">{s.n}</span>
    <span className={`text-[19px] font-semibold leading-[1.4] transition-colors duration-300 ${active ? "text-white" : "text-[#D6D6D6] group-hover:text-white"}`}>
      {s.lead}
    </span>
  </motion.button>
);

const LoopDetail = ({ step, suffix = "" }) => (
  <div className="border border-[#2E2E2E] bg-[#111111] px-7 py-8 min-h-[300px]" data-testid={`loop-detail${suffix}`}>
    <AnimatePresence mode="wait">
      <motion.div
        key={step.n}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
      >
        <span className="font-display text-[22px] text-[#DC2626] leading-none">{step.n}</span>
        <p className="text-[19px] font-semibold text-white leading-[1.4] mt-4">{step.lead}</p>
        <p className="text-[17px] leading-[1.7] text-[#D6D6D6] mt-3">{step.body}</p>
      </motion.div>
    </AnimatePresence>
  </div>
);

const drawY = (origin) => ({
  initial: { scaleY: 0 },
  whileInView: { scaleY: 1 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, ease: "easeOut" },
  className: `absolute top-0 bottom-0 w-px bg-[#2E2E2E] ${origin === "top" ? "left-0 origin-top" : "right-0 origin-bottom"}`,
});

const LoopDiagram = () => {
  const [active, setActive] = useState("1");
  const activate = (n) => { setActive(n); noteLoopStep(Number(n)); };
  const current = LOOP_STEPS.find((s) => s.n === active);
  return (
    <div className="mt-16" data-testid="loop-diagram">
      {/* Desktop: 1–4 descend the left, 5–7 ascend the right, detail in the middle */}
      <div className="hidden md:grid grid-cols-[1fr_1.25fr_1fr] gap-x-10 items-stretch">
        <div className="relative pl-6">
          <motion.div {...drawY("top")} aria-hidden="true" />
          <p className="smallcaps text-[14px] text-white">Getting to them</p>
          <div className="mt-8 space-y-8">
            {LOOP_OUT.map((s, i) => (
              <LoopTitle key={s.n} s={s} index={i} active={active === s.n} onActivate={activate} />
            ))}
          </div>
        </div>
        <LoopDetail step={current} />
        <div className="relative pr-6">
          <motion.div {...drawY("bottom")} aria-hidden="true" />
          <p className="smallcaps text-[14px] text-white">What comes back</p>
          <div className="mt-8 space-y-8">
            {[...LOOP_BACK].reverse().map((s, i) => (
              <LoopTitle key={s.n} s={s} index={i} active={active === s.n} onActivate={activate} />
            ))}
          </div>
        </div>
      </div>
      <Hairline className="hidden md:block mt-12" />
      {/* Mobile: titles in order, detail below */}
      <div className="md:hidden">
        <p className="smallcaps text-[14px] text-white pb-5 border-b border-[#2E2E2E]">Getting to them</p>
        <div className="mt-8 space-y-7">
          {LOOP_OUT.map((s, i) => (
            <LoopTitle key={s.n} s={s} index={i} suffix="-m" active={active === s.n} onActivate={activate} />
          ))}
        </div>
        <p className="smallcaps text-[14px] text-white pb-5 border-b border-[#2E2E2E] mt-12">What comes back</p>
        <div className="mt-8 space-y-7">
          {LOOP_BACK.map((s, i) => (
            <LoopTitle key={s.n} s={s} index={i} suffix="-m" active={active === s.n} onActivate={activate} />
          ))}
        </div>
        <div className="mt-10">
          <LoopDetail step={current} suffix="-m" />
        </div>
      </div>
    </div>
  );
};

/* ---------- page ---------- */

const SMS_CONSENT_LABEL = "I agree to receive text messages from Dane Lomax, LLC at the phone number provided. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. See our Privacy Policy and Terms & Conditions.";

const captureAttribution = () => {
  try {
    if (!localStorage.getItem("dl_attrib")) {
      const p = new URLSearchParams(window.location.search);
      localStorage.setItem("dl_attrib", JSON.stringify({
        referrer: document.referrer || "",
        utm_source: p.get("utm_source") || "",
        utm_medium: p.get("utm_medium") || "",
        utm_campaign: p.get("utm_campaign") || "",
        landing: window.location.pathname + window.location.search,
        at: new Date().toISOString(),
      }));
    }
    return JSON.parse(localStorage.getItem("dl_attrib"));
  } catch {
    return null;
  }
};

const initialAudience = () => {
  try {
    const v = localStorage.getItem("dl_audience");
    return AUDIENCES.some((a) => a.id === v) ? v : "business";
  } catch {
    return "business";
  }
};

const AUDIENCE_RESTORED = (() => {
  try { return AUDIENCES.some((a) => a.id === localStorage.getItem("dl_audience")); } catch { return false; }
})();

export default function Home() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", working_with: "" });
  const [smsConsent, setSmsConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [rulebook, setRulebook] = useState(RULEBOOK_FALLBACK);
  const [audience, setAudience] = useState(initialAudience);
  const [openQuestions, setOpenQuestions] = useState(() => new Set());
  const openedEver = useRef(new Set());
  const mountedAt = useRef(Date.now());
  const lastFieldRef = useRef("");
  const startedRef = useRef(false);
  const submittedRef = useRef(false);
  const abandonSentRef = useRef(false);
  const formSnap = useRef(form);
  formSnap.current = form;

  useEffect(() => {
    mountedAt.current = Date.now();
    captureAttribution();
    initEngagement();
    setSegment(audience);
    if (AUDIENCE_RESTORED) gaEventOnce("segment_select", "segment_restored", { segment: audience, method: "restored" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch(`${API}/seo`).then((r) => r.json()).then((d) => {
      if (d.title) document.title = d.title;
      const m = document.querySelector('meta[name="description"]');
      if (m && d.description) m.setAttribute("content", d.description);
    }).catch(() => {});
    fetch(`${API}/rulebook`).then((r) => r.json()).then((d) => {
      if (Array.isArray(d.items) && d.items.length >= 5) setRulebook(d.items);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    document.getElementById("jsonld-dane")?.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "jsonld-dane";
    s.text = JSON.stringify([
      {
        "@context": "https://schema.org", "@type": "ProfessionalService",
        name: "Dane Lomax", url: window.location.origin,
        description: "Data and enterprise consulting — data operations at scale, infrastructure, and the compliance questions underneath.",
        areaServed: "United States",
      },
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: rulebook.map((r) => ({
          "@type": "Question", name: r.q,
          acceptedAnswer: { "@type": "Answer", text: r.a },
        })),
      },
    ]);
    document.head.appendChild(s);
    return () => s.remove();
  }, [rulebook]);

  useEffect(() => {
    if (analyticsSuppressed()) return;
    const observers = [];
    document.querySelectorAll("[data-ga-section]").forEach((el) => {
      const ratio = Math.min(0.5, (window.innerHeight * 0.5) / Math.max(el.offsetHeight, 1));
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const id = en.target.getAttribute("data-ga-section");
          gaEventOnce("section_view", `section_${id}`, { section_id: id });
          if (id === "compliance") noteComplianceReached();
          obs.unobserve(en.target);
        });
      }, { threshold: ratio });
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  useEffect(() => {
    if (analyticsSuppressed()) return;
    const timers = new Map();
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const id = en.target.getAttribute("data-ga-scenario");
        if (en.isIntersecting && en.intersectionRatio >= 0.5) {
          if (!timers.has(id)) {
            timers.set(id, setTimeout(() => {
              gaEventOnce("scenario_view", `scenario_${id}`, { segment: audience, scenario_id: id });
            }, 3000));
          }
        } else if (timers.has(id)) {
          clearTimeout(timers.get(id));
          timers.delete(id);
        }
      });
    }, { threshold: [0.5] });
    const attach = setTimeout(() => {
      document.querySelectorAll("[data-ga-scenario]").forEach((el) => obs.observe(el));
    }, 450);
    return () => { clearTimeout(attach); obs.disconnect(); timers.forEach(clearTimeout); };
  }, [audience]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState !== "hidden") return;
      if (!startedRef.current || submittedRef.current || abandonSentRef.current) return;
      const f = formSnap.current;
      const completed = ["name", "email", "phone", "working_with"].filter((k) => (f[k] || "").trim()).length;
      abandonSentRef.current = true;
      gaEvent("form_abandon", { last_field: lastFieldRef.current || "none", fields_completed: completed });
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
    };
  }, []);

  const selectAudience = (id, source = "selector") => {
    if (id === audience) return;
    setAudience(id);
    try { localStorage.setItem("dl_audience", id); } catch {}
    setSegment(id);
    track("audience_selected", { segment: id, source });
    gaEvent("segment_select", { segment: id, method: "click" });
  };

  const toggleQuestion = (q, idx) => {
    setOpenQuestions((prev) => {
      const s = new Set(prev);
      if (s.has(q)) s.delete(q); else s.add(q);
      return s;
    });
    if (!openedEver.current.has(q)) {
      openedEver.current.add(q);
      track("legal_question_opened", { question: q });
      noteLegalQuestion(idx + 1);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.working_with.trim()) {
      setError("Name, email, and what you're working with — the last one matters most.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form, website: honeypot, fill_ms: Date.now() - mountedAt.current,
          sms_consent: smsConsent, consent_label: SMS_CONSENT_LABEL, page_url: window.location.href,
          attribution: captureAttribution(),
          audience_segment: audience,
          flags: Array.from(openedEver.current),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "That didn't go through — please try again.");
      setSent(true);
      submittedRef.current = true;
      gaEvent("form_submit", {
        segment: audience, intent_option: audience,
        questions_opened: engagementCounts().questions, loop_steps_opened: engagementCounts().steps,
      });
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  const field = "w-full bg-[#111111] border border-[#2E2E2E] px-4 py-3.5 text-[17px] font-medium text-white placeholder:text-[#D6D6D6] outline-none focus:border-[#FF5C5C] focus:border-2 transition-colors duration-300";
  const label = "block text-[17px] font-medium text-white mb-2";
  const activeGroup = SCENARIO_GROUPS[audience];

  return (
    <MotionConfig reducedMotion="user">
    <div className="min-h-screen bg-[#050505] text-white" data-testid="home-page">

      {/* Navigation */}
      <header className="sticky top-0 z-40 bg-[#050505] border-b border-[#2E2E2E]">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between px-6 md:px-10 h-[76px]">
          <a href="#top" className="leading-tight" data-testid="nav-wordmark">
            <span className="block font-display text-[17px] text-white" style={{ letterSpacing: "0.18em" }}>DANE LOMAX</span>
            <span className="block text-[14px] smallcaps text-[#D6D6D6] mt-0.5">Data &amp; Enterprise Consulting</span>
          </a>
          <nav className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} data-testid={`nav-link-${l.label.toLowerCase().replaceAll(" ", "-")}`}
                className="text-[17px] font-medium text-[#D6D6D6] hover:text-white transition-colors duration-300">
                {l.label}
              </a>
            ))}
          </nav>
          <a href="#contact" data-testid="nav-cta"
            className="border border-[#DC2626] text-white text-[17px] font-medium px-5 py-2.5 min-h-[44px] inline-flex items-center hover:bg-[#DC2626] transition-colors duration-300">
            Work together →
          </a>
        </div>
      </header>

      <main id="top">

        {/* 1 — Hero: 55/45 two-column. Treated portrait (background removed, chest-up crop,
            hard-contrast B&W, feathered edges) over offset #111111/#1A1A1A rectangles
            with a small #DC2626 block. */}
        <section className="px-6 md:px-10 pt-[72px] md:pt-[110px] pb-[72px] md:pb-[130px]" data-testid="hero-section" data-ga-section="hero">
          <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-[11fr_9fr] gap-y-20 lg:gap-x-16 items-center">
            <div>
              <motion.div {...fade}>
                <Eyebrow>Data &amp; Enterprise Consulting</Eyebrow>
              </motion.div>
              <motion.h1 {...fadeAt(1)} className="font-display text-[38px] md:text-[68px] leading-[1.06] text-white mt-8" style={{ letterSpacing: "-0.03em" }} data-testid="hero-headline">
                I get complicated things working.
              </motion.h1>
              <motion.div {...fadeAt(2)} className="mt-10" style={{ width: 64, height: 2, background: "#DC2626" }} />
              <motion.p {...fadeAt(3)} className="text-[21px] leading-[1.6] text-[#D6D6D6] mt-10 max-w-[560px] font-medium" data-testid="hero-support">
                Organizations bring me the problems that don't have an obvious owner yet — data at scale,
                the infrastructure underneath it, and the compliance questions everyone else routed around.
              </motion.p>
              <motion.div {...fadeAt(4)} className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <a href="#contact" data-testid="hero-cta-primary"
                  className="bg-[#FFFFFF] text-[#050505] text-[17px] font-medium px-8 py-4 min-h-[44px] inline-flex items-center hover:bg-[#D6D6D6] transition-colors duration-300">
                  Start a conversation →
                </a>
                <a href="#work" data-testid="hero-cta-secondary"
                  className="text-[17px] font-medium text-[#FF5C5C] underline underline-offset-4 decoration-1 hover:text-white transition-colors duration-300">
                  How the work runs →
                </a>
              </motion.div>
              <motion.p {...fadeAt(5)} className="mt-14 flex items-center gap-3" data-testid="hero-availability">
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: "#DC2626" }} />
                <span className="smallcaps text-[14px] text-[#D6D6D6]">Currently taking a small number of engagements</span>
              </motion.p>
            </div>
            <motion.div {...fadeAt(2)} className="relative w-full max-w-[420px] mx-auto lg:max-w-[500px] lg:justify-self-end" data-testid="hero-portrait">
              <div className="absolute top-0 left-0 w-[82%] h-[84%] bg-[#111111]" aria-hidden="true" />
              <div className="absolute bottom-0 right-0 w-[82%] h-[84%] bg-[#1A1A1A]" aria-hidden="true" />
              <div className="absolute bottom-[9%] -left-3 w-14 h-14 bg-[#DC2626]" aria-hidden="true" />
              <img src="/portrait.png" alt="Dane Lomax" width="650" height="556"
                className="relative z-10 w-full px-4 pt-6" data-testid="hero-portrait-img"
                style={{ filter: "grayscale(1) contrast(1.08) brightness(1.06)" }} />
            </motion.div>
          </div>
        </section>

        {/* Capability strip */}
        <Hairline />
        <section className="px-6 md:px-10" data-testid="capability-strip" data-ga-section="capabilities">
          <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-4 py-[72px] md:py-[96px] gap-y-12">
            <motion.p {...fade} className="font-display text-[28px] leading-[1.25] text-white lg:pr-12">
              Clean data, sound systems, decisions that hold.
            </motion.p>
            {CAPABILITIES.map((c, i) => (
              <motion.div {...fadeAt(i + 1)} key={c.label}
                className="lg:px-12 lg:border-l lg:border-[#2E2E2E]"
                data-testid="capability-column">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-[#2E2E2E] rounded-md">{c.icon}</span>
                <p className="smallcaps text-[14px] text-white mt-5">{c.label}</p>
                <p className="text-[17px] leading-[1.7] text-[#D6D6D6] mt-3">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Proof band — hidden behind SHOW_PROOF_BAND until the real figures arrive */}
        {SHOW_PROOF_BAND && (
          <>
            <Hairline />
            <section className="px-6 md:px-10" data-testid="proof-band">
              <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-y-12 py-[72px] md:py-[96px]">
                {PROOF_FIGURES.map((p, i) => (
                  <motion.div {...fadeAt(i)} key={p.caption} className="md:px-12 md:first:pl-0 md:border-l md:border-[#2E2E2E] md:first:border-l-0" data-testid="proof-figure">
                    <p className="font-display text-[40px] leading-none text-white">{p.figure}</p>
                    <p className="text-[16px] leading-[1.6] text-[#D6D6D6] mt-4">{p.caption}</p>
                  </motion.div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* 2 — How the work runs: the 7-step loop diagram */}
        <Hairline />
        <section id="work" className="px-6 md:px-10 py-[72px] md:py-[140px]" data-testid="work-section" data-ga-section="work">
          <div className="max-w-[1000px] mx-auto">
            <Eyebrow>The method</Eyebrow>
            <div className="mt-6"><SectionHeading>How the work runs</SectionHeading></div>
            <motion.p {...fade} className="text-[19px] leading-[1.7] text-[#D6D6D6] mt-8 max-w-[680px]">
              One continuous operation in two halves that close into a loop. Rest on a step to read it.
            </motion.p>
            <LoopDiagram />
            <motion.p {...fade} className="text-[19px] leading-[1.7] text-[#D6D6D6] mt-16 max-w-[680px] border-l-2 border-[#DC2626] pl-6" data-testid="loop-close">
              <span className="font-semibold text-white">What worked feeds back in. </span>
              Every response teaches me something about who actually converts, and that sharpens the next
              audience instead of starting over.
            </motion.p>
            <motion.p {...fade} className="font-display text-[28px] leading-[1.3] text-white mt-24 max-w-[680px] mx-auto text-center" data-testid="loop-pullquote">
              Most people start at step four and wonder why it isn't working.
            </motion.p>
          </div>
        </section>

        {/* 3 — Scenarios, filtered by the audience selector */}
        <Hairline />
        <section id="scenarios" className="px-6 md:px-10 py-[72px] md:py-[140px]" data-testid="scenarios-section" data-ga-section="scenarios">
          <div className="max-w-[680px] mx-auto">
            <Eyebrow>Situations I get called about</Eyebrow>
            <div className="mt-6"><SectionHeading>You might recognize your week here</SectionHeading></div>
            <motion.div {...fade} className="mt-12 grid grid-cols-2 sm:flex border border-[#2E2E2E]" role="tablist" aria-label="Choose your situation" data-testid="audience-selector">
              {AUDIENCES.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  role="tab"
                  aria-selected={audience === a.id}
                  onClick={() => selectAudience(a.id)}
                  data-testid={`audience-option-${a.id}`}
                  className={`sm:flex-1 px-4 py-3.5 text-[17px] font-medium transition-colors duration-300 ${
                    audience === a.id ? "bg-[#FFFFFF] text-[#050505]" : "text-[#D6D6D6] hover:text-white"
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </motion.div>
            <AnimatePresence mode="wait">
              <motion.div
                key={audience}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="mt-14"
                data-testid="scenario-group"
              >
                <p className="smallcaps text-[14px] text-white pb-5 border-b border-[#2E2E2E]">{activeGroup.group}</p>
                <div className="space-y-12 mt-10">
                  {activeGroup.items.map((s, i) => (
                    <div key={s.title} data-testid="scenario-item" data-ga-scenario={`${audience}_${i + 1}`}>
                      <p className="text-[21px] font-semibold text-white leading-[1.4]">{s.title}</p>
                      <p className="text-[19px] leading-[1.7] text-[#D6D6D6] mt-3">{s.body}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-12">
                  <a href="#contact" className="text-[19px] font-medium text-[#FF5C5C] underline underline-offset-4 decoration-1 hover:text-white transition-colors duration-300">
                    {activeGroup.anchor} →
                  </a>
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </section>

        {/* 4 — How engagements work */}
        <Hairline />
        <section id="engagements" className="px-6 md:px-10 py-[72px] md:py-[140px]" data-testid="engagements-section" data-ga-section="engagements">
          <div className="max-w-[680px] mx-auto">
            <Eyebrow>Working together</Eyebrow>
            <div className="mt-6"><SectionHeading>How engagements work</SectionHeading></div>
            <div className="space-y-12 mt-16">
              {ENGAGEMENT_STEPS.map((s, i) => (
                <motion.div {...fadeAt(i)} key={s.lead} className="flex gap-6" data-testid="engagement-step">
                  <span className="font-display text-[22px] text-[#DC2626] leading-none pt-0.5 w-10 shrink-0">{i + 1}</span>
                  <p className="text-[19px] leading-[1.7] text-[#D6D6D6]">
                    <span className="font-semibold text-white">{s.lead} </span>{s.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 5 — Contact: the agent panel and the form */}
        <Hairline />
        <section id="contact" className="px-6 md:px-10 py-[72px] md:py-[140px]" data-testid="contact-section" data-ga-section="contact">
          <div className="max-w-[680px] mx-auto">
            <Eyebrow>Contact</Eyebrow>
            <div className="mt-6"><SectionHeading>Start a conversation</SectionHeading></div>
            <motion.div {...fade} className="mt-12">
              <ChatPanel />
            </motion.div>

            <motion.div {...fade} className="mt-20" data-testid="enquiry-block">
              <p className="text-[19px] leading-[1.7] text-white font-bold" data-testid="form-framing">
                Or send it straight through. Tell me what you're working with — I read these myself.
              </p>
              {sent ? (
                <div className="border border-[#2E2E2E] bg-[#111111] px-8 py-10 mt-8" data-testid="enquiry-success">
                  <p className="font-display text-[28px] text-white leading-[1.3]">Got it. Thank you.</p>
                  <p className="text-[19px] leading-[1.7] text-[#D6D6D6] mt-4">
                    I'll read it today and come back to you personally — usually the same day, occasionally the next morning.
                    If it's urgent, say so in a reply to my email and it moves up.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-8 space-y-6" data-testid="enquiry-form"
                  onFocus={(e) => {
                    const f = e.target.getAttribute && e.target.getAttribute("data-ga-field");
                    if (f) lastFieldRef.current = f;
                    if (!startedRef.current) { startedRef.current = true; gaEventOnce("form_start", "form_start"); }
                  }}
                  onBlur={(e) => {
                    const f = e.target.getAttribute && e.target.getAttribute("data-ga-field");
                    if (f && e.target.value && e.target.value.trim()) gaEventOnce("form_field_complete", `ffc_${f}`, { field: f });
                  }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className={label} htmlFor="enq-name">Name</label>
                      <input id="enq-name" className={field} value={form.name} data-ga-field="name"
                        onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="enquiry-name-input" />
                    </div>
                    <div>
                      <label className={label} htmlFor="enq-email">Email</label>
                      <input id="enq-email" type="email" className={field} value={form.email} data-ga-field="email"
                        onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enquiry-email-input" />
                    </div>
                  </div>
                  <div>
                    <label className={label} htmlFor="enq-phone">Phone (optional)</label>
                    <input id="enq-phone" className={field} value={form.phone} data-ga-field="phone"
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="enquiry-phone-input" />
                  </div>
                  <fieldset data-testid="form-audience-group">
                    <legend className={label}>Which best describes you?</legend>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {AUDIENCES.map((a) => (
                        <label key={a.id} data-testid={`form-audience-${a.id}`}
                          className={`border px-3 py-3 cursor-pointer text-[17px] font-medium text-center transition-colors duration-300 ${
                            audience === a.id ? "border-[#FF5C5C] text-white" : "border-[#2E2E2E] text-[#D6D6D6] hover:text-white"
                          }`}>
                          <input type="radio" name="audience_segment" value={a.id} checked={audience === a.id}
                            onChange={() => selectAudience(a.id, "form")} className="sr-only" />
                          {a.label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div>
                    <label className={label} htmlFor="enq-text">What are you working with?</label>
                    <textarea id="enq-text" rows={6} className={field} value={form.working_with} data-ga-field="working_with"
                      placeholder="A sentence or two is plenty. What's going wrong, and what have you already tried?"
                      onChange={(e) => setForm({ ...form, working_with: e.target.value })} data-testid="enquiry-text-input" />
                  </div>
                  <input type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                    autoComplete="off" tabIndex={-1} aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} />
                  {error && <p className="text-[17px] font-medium text-[#FF5C5C]" data-testid="enquiry-error">{error}</p>}
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="sms-consent-label">
                    <input type="checkbox" checked={smsConsent}
                      onChange={(e) => { setSmsConsent(e.target.checked); gaEvent("sms_consent_toggle", { checked: e.target.checked }); }}
                      data-testid="sms-consent-checkbox"
                      className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-[#DC2626]" />
                    <span className="text-[16px] font-normal text-[#D6D6D6] leading-[1.6]">
                      I agree to receive text messages from Dane Lomax, LLC at the phone number provided. Message
                      frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help.
                      Consent is not a condition of purchase. See our{" "}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" data-testid="consent-privacy-link"
                        onClick={() => gaEvent("outbound_click", { destination: "privacy" })}
                        className="text-[#FF5C5C] underline underline-offset-4 decoration-1 hover:text-white transition-colors duration-300">Privacy Policy</a>{" "}
                      and{" "}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" data-testid="consent-terms-link"
                        onClick={() => gaEvent("outbound_click", { destination: "terms" })}
                        className="text-[#FF5C5C] underline underline-offset-4 decoration-1 hover:text-white transition-colors duration-300">Terms &amp; Conditions</a>.
                    </span>
                  </label>
                  <button type="submit" disabled={busy} data-testid="enquiry-submit"
                    className="bg-[#FFFFFF] text-[#050505] text-[17px] font-medium px-10 py-4 min-h-[44px] hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-300">
                    {busy ? "Sending..." : "Send it to Dane"}
                  </button>
                  <p className="text-[17px] text-[#D6D6D6]" data-testid="form-promise">
                    I'll come back to you personally, usually the same day.
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </section>

        {/* 6 — The close */}
        <Hairline />
        <section className="px-6 md:px-10 py-[72px] md:py-[140px]" data-testid="close-section" data-ga-section="close">
          <div className="max-w-[680px] mx-auto">
            <motion.p {...fade} className="text-[19px] leading-[1.7] text-[#D6D6D6]" data-testid="close-copy">
              Every step of this work has rules attached. Updating addresses is a postal requirement.
              Removing the deceased before a mailing is basic decency. Calling and texting people carries
              consent obligations with real teeth, and identifying website visitors carries disclosure
              obligations of its own. Most operators skip that part because skipping is faster and nobody
              checks until something goes wrong. I don't — and when someone finally asks, I can show them
              exactly what happened and when.
            </motion.p>
            <motion.p {...fade} className="font-display text-[28px] text-white mt-14" data-testid="discretion-line">
              I don't publish a client list.
            </motion.p>
          </div>
        </section>

        {/* 7 — The counsel block */}
        <Hairline />
        <section id="about" className="px-6 md:px-10 py-[72px] md:py-[140px]" data-testid="counsel-section" data-ga-section="counsel">
          <div className="max-w-[680px] mx-auto">
            <SectionHeading>What I am, and what I'm not.</SectionHeading>
            <motion.div {...fade} className="mt-10 space-y-8" data-testid="counsel-block">
              <p className="text-[19px] leading-[1.7] text-white">
                I'm not an attorney. Nothing here is legal advice, and I don't offer opinions on what the
                law requires. Be careful with anyone in this industry who does.
              </p>
              <p className="text-[19px] leading-[1.7] text-[#D6D6D6]">
                What I have is a compliance function built on standing relationships with counsel who operate
                at the level of entire industries — the lawyers that recognized brands call before they launch,
                not after something goes wrong. Every legal question that comes out of an engagement goes to
                them, and the answer comes back in writing. Then I build to it.
              </p>
              <p className="text-[19px] leading-[1.7] text-[#D6D6D6]" data-testid="counsel-slower-line">
                It means I'm slower than the people who will tell you it's probably fine. It also means you're
                not relying on a vendor's guess about your exposure.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 8 — Messaging compliance */}
        <Hairline />
        <section id="compliance" className="px-6 md:px-10 py-[72px] md:py-[140px]" data-testid="messaging-section" data-ga-section="compliance">
          <div className="max-w-[680px] mx-auto">
            <Eyebrow>Messaging compliance</Eyebrow>
            <div className="mt-6"><SectionHeading>Your texts aren't arriving, and nobody told you.</SectionHeading></div>
            <motion.div {...fade} className="mt-10 space-y-6">
              <p className="text-[19px] leading-[1.7] text-[#D6D6D6]">
                There are two separate ways a messaging program goes wrong, and most people only find out
                about either one after the money is spent. The carriers can quietly filter your traffic
                because your registration doesn't match what you're actually sending. Or you can text someone
                who never properly agreed to hear from you, and the penalties there run per message.
              </p>
              <p className="text-[19px] leading-[1.7] text-white font-bold">I handle both before the first send.</p>
            </motion.div>
            <div className="space-y-10 mt-14">
              {MESSAGING_BLOCKS.map((b, i) => (
                <motion.p {...fadeAt(i)} key={b.lead} className="text-[19px] leading-[1.7] text-[#D6D6D6]" data-testid={b.testid}>
                  <span className="font-semibold text-white">{b.lead} </span>{b.body}
                </motion.p>
              ))}
            </div>
            <motion.p {...fade} className="text-[19px] leading-[1.7] text-[#D6D6D6] mt-14 border-l-2 border-[#DC2626] pl-6" data-testid="messaging-close">
              This is unglamorous work and most vendors skip it, because skipping it is faster and nothing
              happens until something does.
            </motion.p>
            <div className="space-y-12 mt-20">
              {MESSAGING_SCENARIOS.map((s, i) => (
                <motion.div {...fadeAt(i)} key={s.title} data-testid="messaging-scenario">
                  <p className="text-[21px] font-semibold text-white leading-[1.4]">{s.title}</p>
                  <p className="text-[19px] leading-[1.7] text-[#D6D6D6] mt-3">{s.body}</p>
                </motion.div>
              ))}
            </div>
            <AnchorLine>If your messages stopped arriving and nobody can tell you why, tell me what you're working with.</AnchorLine>
          </div>
        </section>

        {/* 9 — The rest of the rulebook, collapsed */}
        <Hairline />
        <section id="rulebook" className="px-6 md:px-10 py-[72px] md:py-[140px]" data-testid="rulebook-section" data-ga-section="rulebook">
          <div className="max-w-[680px] mx-auto">
            <Eyebrow>Before anything goes out</Eyebrow>
            <div className="mt-6"><SectionHeading>The questions I ask first</SectionHeading></div>
            <motion.div {...fade} className="mt-10 space-y-6">
              <p className="text-[19px] leading-[1.7] text-[#D6D6D6]">
                Which rules apply to you depends on what you're doing, where the people live, and what you
                plan to do with the answer.
              </p>
              <p className="text-[19px] leading-[1.7] text-[#D6D6D6]">
                That last one catches the most people. Using data to decide who gets an offer is marketing.
                Using the same data to decide who gets approved is a different body of law entirely, and it
                arrives with obligations most vendors never mention — usually because they don't know either.
              </p>
            </motion.div>
            <div className="mt-16 border-t border-[#2E2E2E]">
              {rulebook.map((r, i) => {
                const open = openQuestions.has(r.q);
                return (
                  <motion.div {...fadeAt(i)} key={r.q} className="border-b border-[#2E2E2E]" data-testid="rulebook-item">
                    <button
                      type="button"
                      onClick={() => toggleQuestion(r.q, i)}
                      aria-expanded={open}
                      data-testid={`rulebook-question-${i}`}
                      className="w-full flex items-baseline justify-between gap-6 text-left py-6 group"
                    >
                      <span className={`text-[19px] font-bold leading-[1.6] transition-colors duration-300 ${open ? "text-white" : "text-[#D6D6D6] group-hover:text-white"}`}>
                        {r.q}
                      </span>
                      <span className="text-[22px] leading-none text-[#FF5C5C] shrink-0 select-none" aria-hidden="true">
                        {open ? "−" : "+"}
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          className="overflow-hidden"
                          data-testid={`rulebook-answer-${i}`}
                        >
                          <p className="text-[19px] leading-[1.7] text-[#D6D6D6] pb-7">{r.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
            <motion.div {...fade} className="mt-16 pt-10 border-t border-[#2E2E2E] space-y-4" data-testid="rulebook-footnote">
              <p className="text-[19px] leading-[1.7] text-[#D6D6D6]">
                Financial services and health data each carry their own federal regimes on top of everything above.
              </p>
              <p className="text-[19px] leading-[1.7] text-[#D6D6D6]">
                Campaigns and committees answer to election regulators as well as consumer ones.
              </p>
              <p className="text-[19px] leading-[1.7] text-[#D6D6D6]">
                Charities answer to the states they solicit in, and to the limits attached to their own tax status.
              </p>
            </motion.div>
            <motion.p {...fade} className="text-[19px] leading-[1.7] text-[#D6D6D6] mt-14" data-testid="rulebook-close">
              <span className="font-semibold text-white">Most people don't have all of these. </span>
              Working out which ones you do have — and which ones you have because of something you did two
              years ago — is most of the job. When one of them needs a legal opinion, it goes to counsel and
              comes back in writing, the way everything here does.
            </motion.p>
            <AnchorLine>If three of those questions made you pause, tell me what you're working with.</AnchorLine>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Hairline />
      <footer className="px-6 md:px-10 py-14" data-testid="footer">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row md:items-center gap-6 md:justify-between">
          <div className="leading-tight">
            <span className="block font-display text-[17px] text-white" style={{ letterSpacing: "0.18em" }}>DANE LOMAX</span>
            <span className="block text-[17px] text-[#D6D6D6] mt-2" data-testid="footer-disclaimer">
              Dane Lomax, LLC is not a law firm and does not provide legal advice.
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-8">
            <a href="/privacy" target="_blank" rel="noopener noreferrer"
              onClick={() => gaEvent("outbound_click", { destination: "privacy" })}
              className="text-[16px] font-medium text-[#D6D6D6] hover:text-white hover:underline underline-offset-4 decoration-1 transition-colors duration-300"
              data-testid="footer-privacy-link">Privacy Policy</a>
            <a href="/terms" target="_blank" rel="noopener noreferrer"
              onClick={() => gaEvent("outbound_click", { destination: "terms" })}
              className="text-[16px] font-medium text-[#D6D6D6] hover:text-white hover:underline underline-offset-4 decoration-1 transition-colors duration-300"
              data-testid="footer-terms-link">Terms &amp; Conditions</a>
            <button type="button"
              onClick={() => { localStorage.removeItem("dl_cookies"); window.dispatchEvent(new Event("dl-cookie-reopen")); }}
              className="text-[16px] font-medium text-[#D6D6D6] hover:text-white hover:underline underline-offset-4 decoration-1 transition-colors duration-300"
              data-testid="footer-cookie-preferences">Cookie preferences</button>
            <a href="/admin"
              className="text-[16px] font-medium text-[#D6D6D6] hover:text-white hover:underline underline-offset-4 decoration-1 transition-colors duration-300"
              data-testid="footer-admin-link">Admin sign-in</a>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
    </MotionConfig>
  );
}
